// Abwesenheiten (Etappe 2): Arten, Genehmigungsstand und die Wirkung auf das
// Stundenkonto. Reine Regeln — Datenzugriffe stehen in api.ts.

export type AbwesenheitTyp =
  | 'urlaub' | 'krank' | 'fza' | 'homeoffice' | 'dienstreise'
  | 'sonderurlaub' | 'unbezahlt' | 'arzttermin';

export type AbwesenheitStatus = 'offen' | 'genehmigt' | 'abgelehnt' | 'storniert';

export interface Abwesenheit {
  id: string;
  mitarbeiter_id: string;
  typ: AbwesenheitTyp;
  von_tag: string;   // 'YYYY-MM-DD'
  bis_tag: string;   // einschließlich
  halber_tag: boolean;
  grund: string | null;
  status: AbwesenheitStatus;
  entschieden_von: string | null;
  entschieden_am: string | null;
  entscheidung_kommentar: string | null;
  nachweis_path: string | null;
  nachweis_name?: string | null;
  /** Aktuelle Freigabestufe (1 = wartet auf die erste Entscheidung) */
  freigabe_stufe?: number;
  created_at: string;
}

/**
 * Wirkung auf die Tagesbilanz:
 *  - 'soll_erfuellt': der Tag gilt als gearbeitet (Urlaub, Krank, Sonderurlaub) → Saldo 0
 *  - 'abbau':         Ist bleibt 0, Soll bleibt stehen (Freizeitausgleich) → baut Plusstunden ab
 *  - 'soll_null':     kein Anspruch, aber auch kein Minus (unbezahlter Urlaub)
 *  - 'keine':         normal gearbeitet, nur Information (Homeoffice, Dienstreise, Arzttermin)
 */
export type Wirkung = 'soll_erfuellt' | 'abbau' | 'soll_null' | 'keine';

export const TYP_INFO: Record<AbwesenheitTyp, {
  label: string;
  wirkung: Wirkung;
  /** Zählt gegen den Jahresurlaub */
  urlaubstage: boolean;
  /** Nachweis (AU) sinnvoll */
  nachweis: boolean;
  farbe: string;
}> = {
  urlaub:       { label: 'Urlaub',              wirkung: 'soll_erfuellt', urlaubstage: true,  nachweis: false, farbe: 'emerald' },
  krank:        { label: 'Krank',               wirkung: 'soll_erfuellt', urlaubstage: false, nachweis: true,  farbe: 'red' },
  fza:          { label: 'Freizeitausgleich',   wirkung: 'abbau',         urlaubstage: false, nachweis: false, farbe: 'sky' },
  homeoffice:   { label: 'Homeoffice',          wirkung: 'keine',         urlaubstage: false, nachweis: false, farbe: 'violet' },
  dienstreise:  { label: 'Dienstreise',         wirkung: 'keine',         urlaubstage: false, nachweis: false, farbe: 'amber' },
  sonderurlaub: { label: 'Sonderurlaub',        wirkung: 'soll_erfuellt', urlaubstage: false, nachweis: false, farbe: 'emerald' },
  unbezahlt:    { label: 'Unbezahlter Urlaub',  wirkung: 'soll_null',     urlaubstage: false, nachweis: false, farbe: 'gray' },
  arzttermin:   { label: 'Arzttermin',          wirkung: 'keine',         urlaubstage: false, nachweis: false, farbe: 'gray' },
};

export const STATUS_INFO: Record<AbwesenheitStatus, { label: string; farbe: string }> = {
  offen:      { label: 'Offen',      farbe: 'amber' },
  genehmigt:  { label: 'Genehmigt',  farbe: 'emerald' },
  abgelehnt:  { label: 'Abgelehnt',  farbe: 'red' },
  storniert:  { label: 'Storniert',  farbe: 'gray' },
};

/** Alle Kalendertage eines Antrags (einschließlich beider Enden). */
export function tageDesAntrags(a: Pick<Abwesenheit, 'von_tag' | 'bis_tag'>): string[] {
  const raus: string[] = [];
  const start = new Date(`${a.von_tag}T12:00:00`);
  const ende = new Date(`${a.bis_tag}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(ende.getTime()) || ende < start) return raus;
  for (const d = start; d <= ende; d.setDate(d.getDate() + 1)) {
    raus.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }
  return raus;
}

/**
 * Die an einem Tag maßgebliche Abwesenheit. Genehmigte schlagen offene, damit ein
 * genehmigter Urlaub nicht von einem später gestellten Antrag überlagert wird;
 * abgelehnte und stornierte zählen nie.
 */
export function abwesenheitAmTag(abwesenheiten: Abwesenheit[], tag: string): Abwesenheit | null {
  const treffer = abwesenheiten.filter(
    (a) => a.status !== 'abgelehnt' && a.status !== 'storniert' && a.von_tag <= tag && a.bis_tag >= tag,
  );
  return treffer.find((a) => a.status === 'genehmigt') || treffer[0] || null;
}

/**
 * Urlaubstage eines Antrags: nur Tage mit Soll (Wochenenden und Feiertage zählen nicht).
 * `sollAmTag` kommt von außen, weil es vom Arbeitszeitmodell abhängt.
 */
export function urlaubstageImAntrag(
  a: Pick<Abwesenheit, 'von_tag' | 'bis_tag' | 'halber_tag'>,
  sollAmTag: (tag: string) => number,
): number {
  const arbeitstage = tageDesAntrags(a).filter((t) => sollAmTag(t) > 0).length;
  if (arbeitstage === 0) return 0;
  return a.halber_tag ? arbeitstage * 0.5 : arbeitstage;
}

/**
 * Urlaubsanspruch einer Person: persönlicher Wert der jüngsten Zuordnung vor dem des
 * Modells, sonst der gesetzliche Rahmen als Rückfall. EINE Regel, weil der Anspruch an
 * mehreren Stellen angezeigt wird (Abwesenheiten, Personalakte) und dort früher
 * getrennt gerechnet wurde — das driftet sonst auseinander.
 */
export function urlaubsanspruch(
  zuordnungen: { modell_id: string; urlaubstage_jahr?: number | null }[],
  modelle: { id: string; urlaubstage_jahr?: number | null }[],
  rueckfall = 30,
): number {
  const letzte = zuordnungen[zuordnungen.length - 1];
  const wert = letzte?.urlaubstage_jahr
    ?? modelle.find((m) => m.id === letzte?.modell_id)?.urlaubstage_jahr
    ?? rueckfall;
  const zahl = Number(wert);
  return Number.isFinite(zahl) && zahl >= 0 ? zahl : rueckfall;
}

export interface Urlaubskonto {
  anspruch: number;
  genommen: number;   // genehmigt
  beantragt: number;  // noch offen
  rest: number;
}

/** Urlaubskonto eines Jahres — offene Anträge mindern den Rest bereits. */
export function urlaubskonto(
  jahr: number,
  anspruch: number,
  abwesenheiten: Abwesenheit[],
  sollAmTag: (tag: string) => number,
): Urlaubskonto {
  const imJahr = abwesenheiten.filter(
    (a) => TYP_INFO[a.typ]?.urlaubstage && a.von_tag.startsWith(String(jahr)),
  );
  const summe = (status: AbwesenheitStatus) => imJahr
    .filter((a) => a.status === status)
    .reduce((s, a) => s + urlaubstageImAntrag(a, sollAmTag), 0);
  const genommen = summe('genehmigt');
  const beantragt = summe('offen');
  return { anspruch, genommen, beantragt, rest: anspruch - genommen - beantragt };
}

/** „12.08.–16.08.2026" bzw. „12.08.2026" für einen Tag. */
export function zeitraumText(a: Pick<Abwesenheit, 'von_tag' | 'bis_tag' | 'halber_tag'>): string {
  const kurz = (t: string) => {
    const d = new Date(`${t}T12:00:00`);
    return Number.isNaN(d.getTime()) ? t : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  if (a.von_tag === a.bis_tag) return `${kurz(a.von_tag)}${a.halber_tag ? ' (halber Tag)' : ''}`;
  return `${kurz(a.von_tag)} – ${kurz(a.bis_tag)}`;
}

/** Prüft einen Antrag vor dem Absenden; gibt den Grund zurück oder null. */
export function antragFehler(a: { von_tag: string; bis_tag: string; typ: AbwesenheitTyp; halber_tag: boolean }): string | null {
  if (!a.von_tag || !a.bis_tag) return 'Bitte Zeitraum angeben.';
  if (a.bis_tag < a.von_tag) return 'Das Ende liegt vor dem Beginn.';
  if (a.halber_tag && a.von_tag !== a.bis_tag) return 'Ein halber Tag geht nur an einem einzelnen Tag.';
  if (!TYP_INFO[a.typ]) return 'Unbekannte Art der Abwesenheit.';
  return null;
}
